/**
 * REGISTRO de vistas de proyección (deuda D5c de la auditoría C3).
 *
 * El manifiesto declara `proyeccion: {vista, opciones}` y la Pantalla dibuja
 * ESA vista: mapa `vista → componente`. La Pantalla no ramifica: resuelve la
 * vista aquí; si no hay componente para la vista declarada cae a la proyección
 * de la primitiva (registro D5b) y, en última instancia, al agregado genérico
 * — nunca en blanco.
 *
 * Sumar una vista nueva = escribir su componente y registrarlo acá.
 * Regla C7 innegociable: una vista SOLO dibuja agregados o contenido anónimo;
 * jamás métricas por persona ni autores.
 */
import type { ComponentType } from 'react';
import type { AgregadoGrupo, Aporte } from '../../lib/sesion';
import { VistaBarras } from './VistaBarras';
import { VistaCuadrantes } from './VistaCuadrantes';
import { VistaGaleria } from './VistaGaleria';
import { VistaMapa } from './VistaMapa';
import { VistaMatriz } from './VistaMatriz';
import { VistaNube } from './VistaNube';

/** Contrato de toda vista de proyección (superficie Pantalla, legible a 6 m). */
export interface PropsVista {
  /** Aportes crudos de la actividad (para tally/galería; sin exponer autores). */
  aportes: Aporte[];
  /** Total de aportes (ya viene del agregado del backend). */
  total: number;
  /** Agregado por grupo del backend (C7). */
  porGrupo: AgregadoGrupo[];
  /** `proyeccion.opciones` del manifiesto, tal cual (títulos, ejes, límites…). */
  opciones: Record<string, unknown>;
}

/**
 * Vistas registradas. `conteo` no aparece: es exactamente el agregado genérico
 * que la Pantalla ya dibuja (total + por grupo), no necesita componente.
 */
export const VISTAS: Record<string, ComponentType<PropsVista>> = {
  barras: VistaBarras,
  cuadrantes: VistaCuadrantes,
  galeria: VistaGaleria,
  mapa: VistaMapa,
  matriz: VistaMatriz,
  nube_viva: VistaNube,
};

/** Componente para una vista declarada, o null si no está registrada. */
export function vistaDe(vista: string | null | undefined): ComponentType<PropsVista> | null {
  if (!vista) return null;
  return VISTAS[vista] ?? null;
}
